import { useEffect, useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import { CheckCircle2, FileSpreadsheet, Send, SlidersHorizontal, Users, X, XCircle } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Chip, ChipGroup } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { Kpi } from '@/components/ui/kpi'
import { Label } from '@/components/ui/label'
import { SkeletonRows } from '@/components/ui/skeleton'

import { REGULAR_BUCKET_LABEL, bucketLabel, dpd, inr, num, priorityColor } from '@/lib/format'
import { cn } from '@/lib/utils'

import { useCaseProducts, useCaseTotals, useCases, type CaseRow } from '@/features/cases/api'
import { CASE_PAGE_SIZE, pageCountFor, useClampPage } from '@/features/cases/paging'
import { PageSummary, PagerButtons } from '@/features/cases/Pager'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import { useOrgTree } from '@/features/admin/orgApi'
import { useMe } from '@/features/auth/api'
import { navPermissionCheck } from '@/components/layout/navVisibility'
import { Link } from '@/lib/router'
import { useAgents, useBulkAllocate, useBulkTransfer, type BulkAllocateResponse } from './api'

type Slot = 'TELE' | 'FIELD'
type AllocState = 'UNALLOCATED' | 'ALLOCATED' | 'ALL'

export function AllocationsPage() {
  // FIELD first: most managers run field teams, and on TELE the agent list
  // holds telecallers only — an empty drop-down read as "no one to assign to".
  const [slot, setSlot] = useState<Slot>('FIELD')
  const [agentId, setAgentId] = useState<string>('')
  const { data: me } = useMe()
  // Excel bulk allocation runs through the ingest pipeline, so it is offered
  // only to those who can upload there.
  const canUpload = navPermissionCheck(me)('INGESTION_UPLOAD')
  const [q, setQ] = useState('')
  const [bucketFilter, setBucketFilter] = useState<string>('ALL')
  const [branchFilter, setBranchFilter] = useState<string>('ALL')
  const [productFilter, setProductFilter] = useState<string>('ALL')
  // Default to the planning pool — "which cases still need an owner on this
  // slot" is what this screen exists for (BUG-003).
  const [allocState, setAllocState] = useState<AllocState>('UNALLOCATED')
  // Keyed by case id, holding the row: a selection survives paging, and the
  // "Outstanding (selected)" card can sum rows that are on other pages.
  const [selected, setSelected] = useState<Map<string, CaseRow>>(new Map())
  const [page, setPage] = useState(0)
  const debouncedQ = useDebouncedValue(q.trim(), 300)
  const [lastResult, setLastResult] = useState<BulkAllocateResponse | null>(null)

  // Every filter and the search are SERVER-side: filtering the fetched page
  // client-side silently hid every match on other pages. The slot chip
  // narrows the allocated/unallocated predicate to that slot. One filter set
  // feeds both the page and the totals so they can't drift.
  const filters = {
    ...(bucketFilter !== 'ALL' ? { bucket: bucketFilter } : {}),
    ...(branchFilter !== 'ALL' ? { branch: branchFilter } : {}),
    ...(productFilter !== 'ALL' ? { product: productFilter } : {}),
    ...(allocState !== 'ALL' ? { allocated: allocState === 'ALLOCATED', slot } : {}),
    ...(debouncedQ ? { q: debouncedQ } : {}),
  }
  const { data: cases, isPending } = useCases(
    { ...filters, limit: CASE_PAGE_SIZE, offset: page * CASE_PAGE_SIZE },
    { keepPrevious: true },
  )
  // The count card and the "of N" cover every matching case, not this page.
  const { data: totals, isPending: totalsPending } = useCaseTotals(filters)
  const rows = cases ?? []
  const total = totals?.total ?? null
  // Any change to what matches starts from the first page.
  const filterKey = JSON.stringify(filters)
  useEffect(() => { setPage(0) }, [filterKey])
  useClampPage(page, setPage, pageCountFor(total))
  const { data: agents } = useAgents(slot)
  // The org tree is already scope-filtered server-side, so this is exactly the
  // branches this user may allocate into. A BRANCH_MANAGER lacks
  // ORG_HIERARCHY_VIEW and gets nothing back — which is fine, they have one
  // branch and the filter hides itself below.
  const { data: orgTree } = useOrgTree()
  // product_type is free text from LOAN_MASTER, so the options are
  // whatever this caller's book actually contains.
  const { data: products } = useCaseProducts(
    allocState !== 'ALL' ? { allocated: allocState === 'ALLOCATED', slot } : {},
  )
  const branches = useMemo(
    () => (orgTree ?? [])
      .filter((n) => n.nodeType === 'BRANCH' && n.status === 'ACTIVE')
      .sort((a, b) => a.code.localeCompare(b.code)),
    [orgTree],
  )
  // Keep whatever is currently selected in the options even if the freshly
  // fetched list no longer contains it, so an in-effect filter can always be
  // seen and cleared rather than silently filtering rows behind a control
  // that has vanished.
  const productOptions = useMemo(() => {
    const opts = new Set(products ?? [])
    if (productFilter !== 'ALL') opts.add(productFilter)
    return [...opts].sort((a, b) => a.localeCompare(b))
  }, [products, productFilter])

  const activeFilterCount =
    (allocState !== 'UNALLOCATED' ? 1 : 0) +
    (bucketFilter !== 'ALL' ? 1 : 0) +
    (branchFilter !== 'ALL' ? 1 : 0) +
    (productFilter !== 'ALL' ? 1 : 0) +
    (q.trim() ? 1 : 0)

  function clearFilters() {
    setAllocState('UNALLOCATED')
    setBucketFilter('ALL')
    setBranchFilter('ALL')
    setProductFilter('ALL')
    setQ('')
  }

  // Switching slot changes who is eligible, so a selection that is no longer
  // in the list must not silently ride along into the submit.
  useEffect(() => {
    if (agentId && agents && !agents.some((a) => a.userId === agentId)) setAgentId('')
  }, [agents, agentId])

  const bulk = useBulkAllocate()
  const transfer = useBulkTransfer()

  // On the Unallocated view a plain allocate is right (an unexpected ACTIVE
  // allocation should fail loudly, not be silently moved). Everywhere else
  // the verb is Reassign — /transfer moves allocated cases and allocates
  // unallocated ones.
  const reassignMode = allocState !== 'UNALLOCATED'
  const activeMutation = reassignMode ? transfer : bulk

  const totalSelected = selected.size
  const totalOutstandingSelected = useMemo(
    () => [...selected.values()]
      .reduce((s, r) => s + (r.currentOutstanding ? Number(r.currentOutstanding) : 0), 0),
    [selected],
  )
  const canSubmit = !!agentId && totalSelected > 0

  function toggleAll(visible: CaseRow[]) {
    setSelected((prev) => {
      const next = new Map(prev)
      const allSelected = visible.every((c) => prev.has(c.caseId))
      if (allSelected) visible.forEach((c) => next.delete(c.caseId))
      else visible.forEach((c) => next.set(c.caseId, c))
      return next
    })
  }

  function toggleOne(row: CaseRow) {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(row.caseId)) next.delete(row.caseId)
      else next.set(row.caseId, row)
      return next
    })
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setLastResult(null)
    activeMutation.mutate(
      { caseIds: Array.from(selected.keys()), slot, agentUserId: agentId },
      {
        onSuccess: (data) => {
          setLastResult(data)
          // Drop successfully-granted cases from selection.
          const failed = new Set(data.failures.map((f) => f.caseId))
          setSelected(new Map([...selected].filter(([id]) => failed.has(id))))
        },
      },
    )
  }

  const errorMessage = activeMutation.isError
    ? isAxiosError(activeMutation.error) && activeMutation.error.response?.status === 403
      ? String(activeMutation.error.response.data?.message ?? '').includes('agent_out_of_scope')
        ? 'That agent is outside your part of the org hierarchy. Pick one of your own agents.'
        : `You do not have ${reassignMode ? 'CASE_REALLOCATE' : 'CASE_ALLOCATE'} permission.`
      : isAxiosError(activeMutation.error) && activeMutation.error.response?.data?.message
      ? activeMutation.error.response.data.message
      : `${reassignMode ? 'Reassign' : 'Bulk allocate'} failed.`
    : null

  return (
    <div className="mx-auto max-w-[1400px] space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">Operations</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-navy-900">Allocations</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Multi-select cases below, pick a slot and an agent. On the Unallocated view the
            action is <strong>Allocate</strong>; on Allocated/All it becomes <strong>Reassign</strong> —
            existing ownership is transferred to the chosen agent, with per-case failures reported.
          </p>
        </div>
        {canUpload && (
          <Link
            to="/ingest"
            // Strict-typed per route in TanStack; routes here are wired by hand.
            search={{ kind: 'ALLOCATION_DELTA' } as never}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-navy-800 hover:border-navy-200 hover:bg-navy-50 active:translate-y-px transition-all duration-fluid ease-fluid motion-reduce:transition-none motion-reduce:active:translate-y-0"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" />
            Upload Excel
          </Link>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          label={allocState === 'UNALLOCATED' ? 'Unallocated cases' : allocState === 'ALLOCATED' ? 'Allocated cases' : 'Open cases'}
          value={num(totals?.total ?? 0)}
          hint={allocState === 'ALL' ? 'In the queue' : `On the ${slot} slot`}
          loading={totalsPending}
          deltaSense="neutral"
        />
        <Kpi label="Selected" value={num(totalSelected)} hint={totalSelected === 0 ? 'Click rows to select' : 'Ready to allocate'} loading={isPending} deltaSense="higher-is-better" />
        <Kpi label="Outstanding (selected)" value={inr(totalOutstandingSelected)} hint="Selected open outstanding" loading={isPending} deltaSense="neutral" />
        <Kpi label="Agents" value={num(agents?.length ?? 0)} hint="Available to allocate to" loading={isPending} deltaSense="neutral" />
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>Allocation controls</CardTitle>
              <CardDescription>Pick once, apply to many cases.</CardDescription>
            </div>
            <Badge tone="navy">{slot}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-[8rem_1fr_auto] items-end">
            <div className="space-y-1.5">
              <Label>Slot</Label>
              <ChipGroup>
                <Chip active={slot === 'TELE'}  onClick={() => setSlot('TELE')}>TELE</Chip>
                <Chip active={slot === 'FIELD'} onClick={() => setSlot('FIELD')}>FIELD</Chip>
              </ChipGroup>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="agent">Agent</Label>
              <select
                id="agent"
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                required
                className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-navy-900 hover:border-navy-200 focus-visible:outline-none focus-visible:border-navy-500 focus-visible:shadow-glow"
              >
                <option value="" disabled>Pick an agent…</option>
                {(agents ?? []).map((a) => (
                  <option key={a.userId} value={a.userId}>
                    {a.empId} — {a.fullName}
                  </option>
                ))}
              </select>
            </div>
            <Button type="submit" disabled={!canSubmit || activeMutation.isPending}>
              <Send className="h-3.5 w-3.5" />
              {activeMutation.isPending
                ? (reassignMode ? 'Reassigning…' : 'Allocating…')
                : `${reassignMode ? 'Reassign' : 'Allocate'} ${totalSelected || ''} ${totalSelected === 1 ? 'case' : 'cases'}`}
            </Button>
          </form>

          {errorMessage && (
            <Alert variant="destructive" className="mt-3">
              <AlertTitle>Bulk allocate failed</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}
          {lastResult && (
            <Alert variant={lastResult.failures.length === 0 ? 'positive' : 'warning'} className="mt-3">
              {lastResult.failures.length === 0
                ? <CheckCircle2 className="h-4 w-4" />
                : <Users className="h-4 w-4" />}
              <AlertTitle>
                {lastResult.grantedCount} granted{lastResult.failures.length > 0 ? ` · ${lastResult.failures.length} failed` : ''}
              </AlertTitle>
              {lastResult.failures.length > 0 && (
                <AlertDescription>
                  Failures stay selected so you can adjust slot or revoke first. Codes:{' '}
                  {Array.from(new Set(lastResult.failures.map((f) => f.code))).join(', ')}
                </AlertDescription>
              )}
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>Open cases</CardTitle>
              <CardDescription>Click rows to select — selections carry across pages.</CardDescription>
            </div>
            <Badge tone="navy">{slot} slot</Badge>
          </div>
          <FilterBar
            activeCount={activeFilterCount}
            onClear={clearFilters}
          >
            <FilterField label="Status">
              <ChipGroup>
                <Chip active={allocState === 'UNALLOCATED'} onClick={() => setAllocState('UNALLOCATED')}>
                  Unallocated
                </Chip>
                <Chip active={allocState === 'ALLOCATED'} onClick={() => setAllocState('ALLOCATED')}>
                  Allocated
                </Chip>
                <Chip active={allocState === 'ALL'} onClick={() => setAllocState('ALL')}>
                  All
                </Chip>
              </ChipGroup>
            </FilterField>

            <FilterField label="Bucket">
              <ChipGroup>
                {(['ALL', 'CURRENT', 'BUCKET_1_30', 'BUCKET_31_60', 'BUCKET_61_90', 'BUCKET_90_PLUS'] as const).map((b) => (
                  <Chip key={b} active={bucketFilter === b} onClick={() => setBucketFilter(b)}>
                    {b === 'ALL' ? 'All' : b === 'CURRENT' ? REGULAR_BUCKET_LABEL : bucketLabel(b)}
                  </Chip>
                ))}
              </ChipGroup>
            </FilterField>

            {/* Selects rather than chips: branch and product counts grow with
                the book, where buckets are a fixed five. A single-branch
                manager is not shown a control with one real choice in it. */}
            {branches.length > 1 && (
              <FilterField label="Branch">
                <FilterSelect
                  ariaLabel="Filter by branch"
                  value={branchFilter}
                  onChange={setBranchFilter}
                  allLabel="All branches"
                  options={branches.map((b) => ({ value: b.code, label: `${b.code} — ${b.name}` }))}
                />
              </FilterField>
            )}

            {productOptions.length > 0 && (
              <FilterField label="Product">
                <FilterSelect
                  ariaLabel="Filter by product"
                  value={productFilter}
                  onChange={setProductFilter}
                  allLabel="All products"
                  options={productOptions.map((pr) => ({ value: pr, label: pr }))}
                />
              </FilterField>
            )}

            <FilterField label="Search" className="sm:col-span-2">
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Loan no, customer, ref, mobile…"
                className="h-8 text-xs"
              />
            </FilterField>
          </FilterBar>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {isPending && <SkeletonRows rows={6} cols={8} />}
          {!isPending && rows.length === 0 && (
            <div className="px-5 py-10 text-center">
              <p className="text-sm font-medium text-navy-900">No matching cases</p>
              <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
                Clear the search, or widen the bucket, branch or product filter.
              </p>
            </div>
          )}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-navy-50/40 text-left text-2xs uppercase tracking-wider text-navy-700">
                  <tr>
                    <th className="px-5 py-2 font-medium">
                      <input
                        type="checkbox"
                        aria-label="Select all on this page"
                        checked={rows.length > 0 && rows.every((c) => selected.has(c.caseId))}
                        onChange={() => toggleAll(rows)}
                      />
                    </th>
                    <th className="px-3 py-2 font-medium">Priority</th>
                    <th className="px-3 py-2 font-medium">Loan</th>
                    <th className="px-3 py-2 font-medium">Customer</th>
                    <th className="px-3 py-2 font-medium">Product</th>
                    <th className="px-3 py-2 font-medium">Branch</th>
                    <th className="px-3 py-2 text-right font-medium">DPD</th>
                    <th className="px-3 py-2 font-medium">Bucket</th>
                    <th className="px-5 py-2 text-right font-medium">Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => {
                    const isSel = selected.has(c.caseId)
                    const pc = priorityColor(c.priorityBand)
                    return (
                      <tr
                        key={c.caseId}
                        onClick={() => toggleOne(c)}
                        className={cn(
                          'border-b border-slate-200 last:border-0 cursor-pointer transition-colors duration-fluid ease-fluid',
                          isSel ? 'bg-navy-50' : 'hover:bg-navy-50/60',
                        )}
                      >
                        <td className="px-5 py-2">
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => toggleOne(c)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Select ${c.loanAccountNo}`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className="inline-flex items-center rounded-md px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wider"
                            style={{ background: pc.bg, color: pc.fg }}
                          >
                            {c.priorityBand ?? '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-navy-900">{c.loanAccountNo}</td>
                        <td className="px-3 py-2 text-navy-900">{c.customerName}</td>
                        <td className="px-3 py-2 text-muted-foreground">{c.productType}</td>
                        <td className="px-3 py-2">
                          {c.branchCode
                            ? <span className="font-mono text-2xs text-navy-800">{c.branchCode}</span>
                            : <span className="text-2xs text-muted-foreground" title="No Branch in the loan master upload">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-navy-900">{dpd(c.currentDpd)}</td>
                        <td className="px-3 py-2"><Badge tone="slate">{bucketLabel(c.currentBucket)}</Badge></td>
                        <td className="px-5 py-2 text-right tabular-nums font-medium text-navy-900">{inr(c.currentOutstanding)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        <CardFooter>
          <span className="text-2xs uppercase tracking-wider text-muted-foreground">
            <PageSummary page={page} rowsOnPage={rows.length} total={total} /> · {totalSelected} selected · slot {slot}
          </span>
          <span className="inline-flex items-center gap-3">
            <PagerButtons page={page} setPage={setPage} rowsOnPage={rows.length} total={total} />
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Map())} disabled={totalSelected === 0}>
              <XCircle className="h-3.5 w-3.5" />
              Clear selection
            </Button>
          </span>
        </CardFooter>
      </Card>
    </div>
  )
}

/**
 * The filter bar. A labelled grid rather than one long inline row: the row
 * worked while there were two filters and stopped scanning as soon as branch
 * and product were added to it.
 */
function FilterBar({
  activeCount,
  onClear,
  children,
}: {
  activeCount: number
  onClear: () => void
  children: React.ReactNode
}) {
  return (
    <div className="mt-4 rounded-md border border-slate-200 bg-navy-50/30 p-3">
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wider text-navy-700">
          <SlidersHorizontal className="h-3 w-3" />
          Filters
          {activeCount > 0 && <Badge tone="navy">{activeCount}</Badge>}
        </span>
        {/* Only offered when something is actually on, so it never reads as a
            live control that does nothing. */}
        {activeCount > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="inline-flex items-center gap-1 rounded text-2xs font-medium uppercase tracking-wider text-navy-700 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:underline"
          >
            <X className="h-3 w-3" />
            Clear all
          </button>
        )}
      </div>
      <div className="grid gap-x-5 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
        {children}
      </div>
    </div>
  )
}

function FilterField({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn('min-w-0 space-y-1.5', className)}>
      <span className="block text-2xs uppercase tracking-wider text-navy-600">{label}</span>
      {children}
    </div>
  )
}

function FilterSelect({
  ariaLabel,
  value,
  onChange,
  allLabel,
  options,
}: {
  ariaLabel: string
  value: string
  onChange: (v: string) => void
  allLabel: string
  options: { value: string; label: string }[]
}) {
  return (
    <select
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 w-full truncate rounded-md border border-slate-200 bg-white px-2 text-xs text-navy-900 hover:border-navy-200 focus-visible:border-navy-500 focus-visible:shadow-glow focus-visible:outline-none"
    >
      <option value="ALL">{allLabel}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  )
}
